import { Box, Skeleton, Typography } from "@mui/material";
import { CoreCard } from "@verity/components/core";
import i18n from "../../i18n";
import ClockIcon from "../../home/assets/clock.svg?react";

const FLAG_LEGEND = [
	{
		key: "minor",
		color: "#22C55E",
		label: i18n.t("instructor.components.reports.integrity.breakdown.minorFlag"),
		points: i18n.t("instructor.components.reports.integrity.breakdown.minorPoints"), // "-1 pt each"
		description: i18n.t("instructor.components.reports.integrity.breakdown.minorDesc"),
	},
	{
		key: "major",
		color: "#F59E0B",
		label: i18n.t("instructor.components.reports.integrity.breakdown.majorFlag"),
		points: i18n.t("instructor.components.reports.integrity.breakdown.majorPoints"), // "-2 pt each"
		description: i18n.t("instructor.components.reports.integrity.breakdown.majorDesc"),
	},
	{
		key: "severe",
		color: "#EF4444",
		label: i18n.t("instructor.components.reports.integrity.breakdown.severeFlag"),
		points: i18n.t("instructor.components.reports.integrity.breakdown.severePoints"), // "-5 pt each"
		description: i18n.t("instructor.components.reports.integrity.breakdown.severeDesc"),
	},
];

function getRiskLabel(score: number) {
	if (score < 50) return i18n.t("instructor.components.reports.integrity.breakdown.highRisk");
	if (score < 70) return i18n.t("instructor.components.reports.integrity.breakdown.atThreshold");
	return i18n.t("instructor.components.reports.integrity.breakdown.good");
}

export function getScoreColors(score: number | null) {
	if (score === null || score < 50) {
		return { text: "#B91C1C", chipBg: "#FEE2E2", chipText: "#991B1B" };
	}
	if (score < 70) {
		return { text: "#8A6D1D", chipBg: "#FEF3C7", chipText: "#92600A" };
	}
	return { text: "#166534", chipBg: "#DCFCE7", chipText: "#166534" };
}

export function IntegrityScoreBreakdown({ score, isLoading }: { score: number | null; isLoading: boolean }) {
	const riskLabel = score !== null ? getRiskLabel(score) : "N/A";
	const scoreColors = getScoreColors(score);
	// Clamp for marker positioning along the 0-100 gradient bar
	const markerPosition = score !== null ? score : 0;

	return (
		<CoreCard
			appearance="settings"
			useHeader
			icon={<ClockIcon />}
			title={i18n.t("instructor.components.reports.integrity.breakdown.title")}
			style={{ width: "100%", height: "100%" }}
			slotProps={{ bodyWrapper: { style: { padding: 0 } } }}
		>
			<Box sx={{ padding: "20px 16px", display: "flex", flexDirection: "column", gap: "20px" }}>
				<Box display="flex" gap="16px">
					<Box
						sx={{
							width: "25%",
							flexShrink: 0,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							textAlign: "center",
							gap: "8px",
						}}
					>
						{isLoading ? (
							<Skeleton variant="text" width={64} height={48} />
						) : (
							<Box display="flex" alignItems="baseline" gap="4px">
								<Typography sx={{ fontSize: "40px", fontWeight: 700, color: scoreColors.text, lineHeight: 1 }}>
									{score ?? "N/A"}
								</Typography>
								{score !== null && (
									<Typography sx={{ fontSize: "16px", fontWeight: 500, color: scoreColors.text }}>/100</Typography>
								)}
							</Box>
						)}

						{isLoading ? (
							<Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: "100px" }} />
						) : (
							<Box
								sx={{
									backgroundColor: scoreColors.chipBg,
									color: scoreColors.chipText,
									fontSize: "12px",
									fontWeight: 600,
									padding: "2px 10px",
									borderRadius: "100px",
								}}
							>
								{riskLabel}
							</Box>
						)}
					</Box>

					<Box sx={{ width: "75%", display: "flex", flexDirection: "column", gap: "12px" }}>
						<Box display="flex" flexDirection="column" gap="4px">
							<Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#262626" }}>
								{i18n.t("instructor.components.reports.integrity.breakdown.scoreScale")}
							</Typography>
							<Typography sx={{ fontSize: "13px", color: "#6B6B6B" }}>
								{i18n.t("instructor.components.reports.integrity.breakdown.scoreScaleDesc")}
							</Typography>
						</Box>

						<Box sx={{ position: "relative", height: "8px", borderRadius: "4px", overflow: "visible" }}>
							<Box
								sx={{
									height: "100%",
									borderRadius: "4px",
									background: "linear-gradient(to right, #EF4444, #F59E0B, #22C55E)",
								}}
							/>
							<Box
								sx={{
									position: "absolute",
									top: "-2px",
									left: `${markerPosition}%`,
									transform: "translateX(-50%)",
									width: "2px",
									height: "12px",
									backgroundColor: "#262626",
								}}
							/>
						</Box>

						<Box display="flex" justifyContent="space-between" sx={{ fontSize: "12px", color: "#6B6B6B" }}>
							<span>{i18n.t("instructor.components.reports.integrity.breakdown.highRiskRange")}</span>
							<span>{i18n.t("instructor.components.reports.integrity.breakdown.thresholdRange")}</span>
							<span>{i18n.t("instructor.components.reports.integrity.breakdown.goodRange")}</span>
						</Box>

						<Typography sx={{ fontSize: "13px", color: "#6B6B6B" }}>
							{i18n.t("instructor.components.reports.integrity.breakdown.belowThresholdNote")}
						</Typography>
					</Box>
				</Box>

				<Box sx={{ borderTop: "1px solid #EFEFEF" }} />

				<Box display="flex" flexDirection="column" gap="12px">
					<Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#262626" }}>
						{i18n.t("instructor.components.reports.integrity.breakdown.howCalculated")}
					</Typography>
					<Typography sx={{ fontSize: "13px", color: "#6B6B6B" }}>
						{i18n.t("instructor.components.reports.integrity.breakdown.howCalculatedDesc")}
					</Typography>

					{FLAG_LEGEND.map((flag) => (
						<Box key={flag.key} display="flex" flexDirection="column" gap="2px">
							<Box display="flex" alignItems="center" justifyContent="space-between">
								<Box display="flex" alignItems="center" gap="8px">
									<Box
										sx={{
											width: "8px",
											height: "8px",
											borderRadius: "50%",
											backgroundColor: flag.color,
											flexShrink: 0,
										}}
									/>
									<Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#262626" }}>{flag.label}</Typography>
								</Box>
								<Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#262626" }}>{flag.points}</Typography>
							</Box>
							<Typography sx={{ fontSize: "12px", color: "#6B6B6B", pl: "16px" }}>{flag.description}</Typography>
						</Box>
					))}
				</Box>
			</Box>
		</CoreCard>
	);
}
